import { SxProps, Table, TableContainer, TableProps, Theme } from "@mui/material";
import { forwardRef } from "react";

type StickyTableProps = TableProps & {
  // The rows scroll inside the container once the table grows taller than
  // this. The header only stays put while the table scrolls inside its box,
  // so the default cap is low enough for a day's 24 rows to need scrolling.
  maxHeight?: number | string;
};

// Sticky cells must paint their own background, or the rows passing underneath
// show through. MUI paints sticky headers with the default background; these
// tables sit on paper.
const stickySx: SxProps<Theme> = {
  borderCollapse: "separate",
  "&& th": { backgroundColor: "background.paper", whiteSpace: "nowrap" },
  "&& th:first-of-type, & td:first-of-type": {
    position: "sticky",
    left: 0,
    backgroundColor: "background.paper",
    whiteSpace: "nowrap",
    borderRight: 1,
    borderColor: "divider",
  },
  // The corner cell sits above both the header row and the first column.
  "&& th:first-of-type": { zIndex: 3 },
  "& td:first-of-type": { zIndex: 1 },
};

// A table whose header stays visible while the rows scroll and whose first
// column — the period — stays visible while they scroll sideways. Both need the
// table to scroll inside its own box, hence the capped container height. A row
// that highlights itself has to repaint its first cell as well:
// `sx={{ backgroundColor: c, "&& td:first-of-type": { backgroundColor: c } }}`.
export const StickyTable = forwardRef<HTMLDivElement, StickyTableProps>(
  ({ maxHeight = "min(60vh, 480px)", sx, children, ...props }, ref) => (
    <TableContainer ref={ref} sx={{ maxHeight }}>
      <Table
        size="small"
        stickyHeader
        {...props}
        sx={[stickySx, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {children}
      </Table>
    </TableContainer>
  ),
);
StickyTable.displayName = "StickyTable";
